import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import { pushNotification } from '../../state/slices/notificationSlice';
import { NotificationType } from '../../components/notifications/notification-component';
import { useDispatch } from 'react-redux';
import { KeyValuePair } from '../../models/key-value-pair';
import { REACT_APP_VERSION } from '../configuration';
import { set } from 'lodash';

export enum LightCondition {
  DARK = 'It was dark or nighttime',
}
export enum SafetyEquipmentUsed {
  CHILD_SAFETY_BOOSTER_SEAT = 'Child safety or booster seat',
  NONE = 'None',
}
export enum PositionInVehicle {
  LEFT_FRONT = 'Front Row-Left Seat',
}

export enum CollisionType {
  FIXED_OBJECT = 'Fixed Object',
  NON_FIXED_OBJECT = 'Other/Specify',
}

export enum ReportStatus {
  IN_PROGRESS = 'In progress',
  SUBMITTED = 'Submitted',
  UNDER_REVIEW = 'Under review',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
}

export enum PrimaryEvent {
  STRUCK_OBJECT = 'Struck Object',
}

export enum CollisionTypes {
  ANIMAL = 'animal',
  HIT_AND_RUN = 'hit-and-run',
  OBJECT_ON_ROADWAY = 'object-on-roadway',
  LEFT_SIDE_ROAD = 'left-side-road',
  RIGHT_SIDE_ROAD = 'right-side-road',
}

export enum AnimalType {
  OTHER = 'Other Animal',
}

export enum ObjectIdentification {
  ANIMAL = 'Animal',
  FIXED_OBJECT = 'Fixed Object',
  OTHER = 'Other/Specify',
}

export enum DirectionOfTravel {
  NORTH = 'North',
  SOUTH = 'South',
  EAST = 'East',
  WEST = 'West',
  UNKNOWN = 'Unknown',
}

export enum RoadClass {
  UNDIVIDED_ONE_WAY = 'Undivided One-Way',
  UNDIVIDED_TWO_WAY = 'Undivided Two-Way',
  DIVIDED_NO_BARRIER = 'Divided No Barrier',
  DIVIDED_WITH_BARRIER = 'Divided With Barrier',
  OTHER_SPECIFY = 'Other/Specify',
  UNKNOWN = 'Unknown',
  BLANK = 'Blank',
}

export enum CollisionLocation {
  INTERSECTION_INTERSECTION_RELATED = 'Intersection/Intersection-Related',
  NON_INTERSECTION = 'Non-Intersection',
  AT_NEAR_RR_XING = 'At/Near R.R.Xing',
  OTHER_SPECIFY = 'Other/Specify',
  NOT_APPLICABLE = 'Not Applicable',
  UNKNOWN = 'Unknown',
  BLANK = 'Blank',
}

export enum InitialPointOfImpact {
  FRONT = 'Front centre area',
  RIGHTFRONT = 'Right front area',
  RIGHTMIDDLE = 'Right centre area',
  RIGHTREAR = 'Right rear area',
  BACK = 'Back centre area',
  LEFTFRONT = 'Left front area',
  LEFTMIDDLE = 'Left centre area',
  LEFTREAR = 'Left rear area',
  TOP = 'Top of passenger area',
  UNDERCARRIAGE = 'Undercarriage',
  ROLLOVER = 'Rollover',
  ATTACHMENT = 'Load/trailer attachment',
}

export enum Attachment {
  RECREATION_TRAILER = 'Recreation Trailer',
  SMALL_UTILITY_TRAILER = 'Small Utility Trailer',
  FARM_EQUIPMENT = 'Farm Equipment',
  TOWED_MOTOR_VEHICLE = 'Towed Motor Vehicle',
  OTHER = 'Other/Specify',
  UNKNOWN = 'Unknown',
}

export enum LoadDetails {
  LOADED = 'Yes',
  UNLOADED = 'No',
  NOT_APPLICABLE = 'Not Applicable',
  UNKNOWN = 'Unknown',
}

export enum LoadSpillate {
  LOAD_SPILLED = 'Yes',
  LOAD_NOT_SPILLED = 'No',
  NOT_APPLICABLE = 'Not Applicable',
  UNKNOWN = 'Unknown',
}

export enum LightConditionA {
  DAYLIGHT = 'Daylight',
  SUNGLARE = 'Sunglare',
  DARKNESS = 'Darkness',
}

export enum LightConditionsB {
  ARTIFICIAL_LIGHT = 'Artificial Light',
  NO_ARTIFICIAL_LIGHT = 'No Artificial Light',
  UNKNOWN = 'Unknown',
}

export const isHitAndRun = (
  id: number,
  configuration: ConfigurationState,
): boolean => {
  return (
    id ==
    configuration.configuration.collisionType.find(
      (x) => x.value == CollisionTypes.HIT_AND_RUN,
    )?.id
  );
};

export const isAttachment = (
  id: number,
  configuration: ConfigurationState,
): boolean => {
  return (
    id ==
    configuration.configuration.pointOfImpact.find(
      (x) => x.value == InitialPointOfImpact.ATTACHMENT,
    )?.id
  );
};

export const hasLoad = (
  id: number,
  configuration: ConfigurationState,
): boolean => {
  return (
    id ==
    configuration.configuration.loadDetails.find(
      (x) => x.value == LoadDetails.LOADED,
    )?.id
  );
};

export const isSpilled = (
  id: number,
  configuration: ConfigurationState,
): boolean => {
  return (
    id ==
    configuration.configuration.loadSpillage.find(
      (x) => x.value == LoadSpillate.LOAD_SPILLED,
    )?.id
  );
};

export const isOtherAttachmentType = (
  id: number,
  configuration: ConfigurationState,
): boolean => {
  return (
    id ==
    configuration.configuration.attachments.find(
      (x) => x.value == Attachment.OTHER,
    )?.id
  );
};

export const isStruckObject = (
  id: number,
  configuration: ConfigurationState,
): boolean => {
  return (
    id ==
    configuration.configuration.primaryEvent.find(
      (x) => x.value == PrimaryEvent.STRUCK_OBJECT,
    )?.id
  );
};

export const getConfigurationByDescription = <T>(
  configuration: KeyValuePair<number, string>[],
  description: T,
): KeyValuePair<number, string> | undefined => {
  return (
    configuration.find((element) => element.value === description) || undefined
  );
};

export const showReportAsArchivedAfterDays = 30;
export const openCollisionValidForDays = 60;

export interface ConfigurationState {
  uiVersion?: string;
  csrApiVersion?: string;
  eCollisionApiVersion?: string;
  uiTriggers: {
    reviewDamageModified?: boolean;
    reviewPointOfImpactModified?: boolean;
    reviewMediaUpdated?: boolean;
    collisionTypeUpdated?: boolean;
    collisionSubTypeUpdated?: boolean;
  };
  configuration: {
    primaryEvent: KeyValuePair<number, string>[];
    objectIdentification: KeyValuePair<number, string>[];
    objectType: KeyValuePair<number, string>[];
    collisionType: KeyValuePair<number, string>[];
    pointOfImpact: KeyValuePair<number, string>[];
    attachments: KeyValuePair<number, string>[];
    loadDetails: KeyValuePair<number, string>[];
    loadSpillage: KeyValuePair<number, string>[];
    directionOfTravel: KeyValuePair<number, string>[];
    specialFacilities: KeyValuePair<number, string>[];
    collisionLocation: KeyValuePair<number, string>[];
    roadAlignmentA: KeyValuePair<number, string>[];
    roadAlignmentB: KeyValuePair<number, string>[];
    travelingLane: KeyValuePair<number, string>[];
    roadClasses: KeyValuePair<number, string>[];
    genders: KeyValuePair<number, string>[];
    positionInVehicle: KeyValuePair<number, string>[];
    safetyEquipment: KeyValuePair<number, string>[];
    vehicleColours: KeyValuePair<number, string>[];
    provinceOrState: KeyValuePair<number, string>[];
    contributingRoadCondition: KeyValuePair<number, string>[];
    surfaceCondition: KeyValuePair<number, string>[];
    trafficControlDevice: KeyValuePair<number, string>[];
    trafficControlCondition: KeyValuePair<number, string>[];
    vehicleCondition: KeyValuePair<number, string>[];
    weatherCondition: KeyValuePair<number, string>[];
    lightConditionA: KeyValuePair<number, string>[];
    lightConditionB: KeyValuePair<number, string>[];
    collisionReportStatuses: KeyValuePair<number, string>[];
    animalType: KeyValuePair<number, string>[];
    otherAnimalType: KeyValuePair<number, string>[];
  };
  isLoading: boolean;
  error: string | null;
  isLoaded?: boolean;
}

const initialState: ConfigurationState = {
  uiVersion: REACT_APP_VERSION ?? '',
  csrApiVersion: '',
  eCollisionApiVersion: '',
  uiTriggers: {},
  configuration: {
    primaryEvent: [],
    objectIdentification: [],
    objectType: [],
    collisionType: [],
    pointOfImpact: [],
    attachments: [],
    loadDetails: [],
    loadSpillage: [],
    directionOfTravel: [],
    specialFacilities: [],
    collisionLocation: [],
    roadAlignmentA: [],
    roadAlignmentB: [],
    travelingLane: [],
    roadClasses: [],
    genders: [],
    positionInVehicle: [],
    safetyEquipment: [],
    vehicleColours: [],
    provinceOrState: [],
    contributingRoadCondition: [],
    surfaceCondition: [],
    trafficControlDevice: [],
    trafficControlCondition: [],
    vehicleCondition: [],
    weatherCondition: [],
    lightConditionA: [],
    lightConditionB: [],
    collisionReportStatuses: [],
    animalType: [],
    otherAnimalType: [],
  },
  isLoading: false,
  error: null,
  isLoaded: false,
};

export const fetchConfiguration = createAsyncThunk(
  'configuration/fetchConfiguration',
  async (csrService: AxiosInstance, { rejectWithValue }) => {
    try {
      const processResponse = (response: PromiseSettledResult<any>) =>
        response.status === 'fulfilled' ? response.value.data : null;

      const [csrApiVersion, config] = await Promise.allSettled([
        csrService.get('/api/health'),
        csrService.get('/api/v1/config'),
      ]);

      return {
        apiVersion: processResponse(csrApiVersion)?.version ?? '',
        eCollisionApiVersion:
          processResponse(csrApiVersion)?.eCollisionApiVersion ?? '',
        configuration: processResponse(config) ?? '',
      };
    } catch (error: any) {
      const dispatch = useDispatch();
      dispatch(
        pushNotification({
          body: 'Failed to fetch configuration',
          notificationType: NotificationType.importantGenericError,
        }),
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const configurationSlice = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    resetReviewUIFlowState: (state) => {
      state.uiTriggers = {};
    },
    setReviewDamageModified: (state, action: PayloadAction<boolean>) => {
      state.uiTriggers.reviewDamageModified = action.payload;
    },
    setReviewPointOfImpactModified: (state, action: PayloadAction<boolean>) => {
      state.uiTriggers.reviewPointOfImpactModified = action.payload;
    },
    setReviewMediaUpdated: (state, action: PayloadAction<boolean>) => {
      state.uiTriggers.reviewMediaUpdated = action.payload;
    },
    setCollisionTypeUpdated: (state, action: PayloadAction<boolean>) => {
      state.uiTriggers.collisionTypeUpdated = action.payload;
    },
    setCollisionSubTypeUpdated: (state, action: PayloadAction<boolean>) => {
      state.uiTriggers.collisionSubTypeUpdated = action.payload;
    },
    // Add other reducers as needed
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConfiguration.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchConfiguration.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.csrApiVersion = action.payload.apiVersion;
          state.uiTriggers = {};

          state.eCollisionApiVersion = action.payload.eCollisionApiVersion;
          state.configuration.objectIdentification = [];
          if (action.payload.configuration.objectIdentification)
            action.payload.configuration.objectIdentification.forEach(
              (element: any) => {
                state.configuration.objectIdentification.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.primaryEvent = [];
          if (action.payload.configuration.primaryEvent)
            action.payload.configuration.primaryEvent.forEach(
              (element: any) => {
                state.configuration.primaryEvent.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.collisionType = [];
          if (action.payload.configuration.collisionType)
            action.payload.configuration.collisionType.forEach(
              (element: any) => {
                state.configuration.collisionType.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.objectType = [];
          if (action.payload.configuration.objectType)
            action.payload.configuration.objectType.forEach((element: any) => {
              state.configuration.objectType.push({
                id: element.id,
                value: element.description,
                formLabel: element.formLabel,
                reviewPageLabel: element.reviewPageLabel,
                isActive: element.isActive,
              });
            });
          state.configuration.pointOfImpact = [];
          if (action.payload.configuration.pointOfImpact)
            action.payload.configuration.pointOfImpact.forEach(
              (element: any) => {
                state.configuration.pointOfImpact.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.attachments = [];
          if (action.payload.configuration.attachments)
            action.payload.configuration.attachments.forEach((element: any) => {
              state.configuration.attachments.push({
                id: element.id,
                value: element.description,
                formLabel: element.formLabel,
                reviewPageLabel: element.reviewPageLabel,
                isActive: element.isActive,
              });
            });
          state.configuration.loadDetails = [];
          if (action.payload.configuration.loadDetails)
            action.payload.configuration.loadDetails.forEach((element: any) => {
              state.configuration.loadDetails.push({
                id: element.id,
                value: element.description,
                formLabel: element.formLabel,
                reviewPageLabel: element.reviewPageLabel,
                isActive: element.isActive,
              });
            });
          state.configuration.loadSpillage = [];
          if (action.payload.configuration.loadSpillage)
            action.payload.configuration.loadSpillage.forEach(
              (element: any) => {
                state.configuration.loadSpillage.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.directionOfTravel = [];
          if (action.payload.configuration.directionOfTravel)
            action.payload.configuration.directionOfTravel.forEach(
              (element: any) => {
                state.configuration.directionOfTravel.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.specialFacilities = [];
          if (action.payload.configuration.specialFacilities)
            action.payload.configuration.specialFacilities.forEach(
              (element: any) => {
                state.configuration.specialFacilities.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.collisionLocation = [];
          if (action.payload.configuration.collisionLocation)
            action.payload.configuration.collisionLocation.forEach(
              (element: any) => {
                state.configuration.collisionLocation.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.roadAlignmentA = [];
          if (action.payload.configuration.roadAlignmentA)
            action.payload.configuration.roadAlignmentA.forEach(
              (element: any) => {
                state.configuration.roadAlignmentA.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.roadAlignmentB = [];
          if (action.payload.configuration.roadAlignmentB)
            action.payload.configuration.roadAlignmentB.forEach(
              (element: any) => {
                state.configuration.roadAlignmentB.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.travelingLane = [];
          if (action.payload.configuration.travelingLane)
            action.payload.configuration.travelingLane.forEach(
              (element: any) => {
                state.configuration.travelingLane.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.roadClasses = [];
          if (action.payload.configuration.roadClasses)
            action.payload.configuration.roadClasses.forEach((element: any) => {
              state.configuration.roadClasses.push({
                id: element.id,
                value: element.description,
                formLabel: element.formLabel,
                reviewPageLabel: element.reviewPageLabel,
                isActive: element.isActive,
              });
            });
          state.configuration.genders = [];
          if (action.payload.configuration.genders)
            action.payload.configuration.genders.forEach((element: any) => {
              state.configuration.genders.push({
                id: element.id,
                value: element.description,
                formLabel: element.formLabel,
                reviewPageLabel: element.reviewPageLabel,
                isActive: element.isActive,
              });
            });
          state.configuration.positionInVehicle = [];
          if (action.payload.configuration.positionInVehicle)
            action.payload.configuration.positionInVehicle.forEach(
              (element: any) => {
                state.configuration.positionInVehicle.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.safetyEquipment = [];
          if (action.payload.configuration.safetyEquipmentUsed)
            action.payload.configuration.safetyEquipmentUsed.forEach(
              (element: any) => {
                state.configuration.safetyEquipment.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.vehicleColours = [];
          if (action.payload.configuration.vehicleColours)
            action.payload.configuration.vehicleColours.forEach(
              (element: any) => {
                state.configuration.vehicleColours.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.provinceOrState = [];
          if (action.payload.configuration.provinceOrState)
            action.payload.configuration.provinceOrState.forEach(
              (element: any) => {
                state.configuration.provinceOrState.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.contributingRoadCondition = [];
          if (action.payload.configuration.contributingRoadCondition)
            action.payload.configuration.contributingRoadCondition.forEach(
              (element: any) => {
                state.configuration.contributingRoadCondition.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.surfaceCondition = [];
          if (action.payload.configuration.surfaceCondition)
            action.payload.configuration.surfaceCondition.forEach(
              (element: any) => {
                state.configuration.surfaceCondition.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.trafficControlDevice = [];
          if (action.payload.configuration.trafficControlDevice)
            action.payload.configuration.trafficControlDevice.forEach(
              (element: any) => {
                state.configuration.trafficControlDevice.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.trafficControlCondition = [];
          if (action.payload.configuration.trafficControlCondition)
            action.payload.configuration.trafficControlCondition.forEach(
              (element: any) => {
                state.configuration.trafficControlCondition.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.vehicleCondition = [];
          if (action.payload.configuration.vehicleCondition)
            action.payload.configuration.vehicleCondition.forEach(
              (element: any) => {
                state.configuration.vehicleCondition.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.weatherCondition = [];
          if (action.payload.configuration.environmentalConditions)
            action.payload.configuration.environmentalConditions.forEach(
              (element: any) => {
                state.configuration.weatherCondition.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.lightConditionA = [];
          if (action.payload.configuration.lightConditionA)
            action.payload.configuration.lightConditionA.forEach(
              (element: any) => {
                state.configuration.lightConditionA.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.lightConditionB = [];
          if (action.payload.configuration.lightConditionB)
            action.payload.configuration.lightConditionB.forEach(
              (element: any) => {
                state.configuration.lightConditionB.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.collisionReportStatuses = [];
          if (action.payload.configuration.collisionReportStatuses)
            action.payload.configuration.collisionReportStatuses.forEach(
              (element: any) => {
                state.configuration.collisionReportStatuses.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.configuration.animalType = [];
          if (action.payload.configuration.animalType)
            action.payload.configuration.animalType.forEach((element: any) => {
              state.configuration.animalType.push({
                id: element.id,
                value: element.description,
                formLabel: element.formLabel,
                reviewPageLabel: element.reviewPageLabel,
                isActive: element.isActive,
              });
            });
          state.configuration.otherAnimalType = [];
          if (action.payload.configuration.otherAnimalType)
            action.payload.configuration.otherAnimalType.forEach(
              (element: any) => {
                state.configuration.otherAnimalType.push({
                  id: element.id,
                  value: element.description,
                  formLabel: element.formLabel,
                  reviewPageLabel: element.reviewPageLabel,
                  isActive: element.isActive,
                });
              },
            );
          state.isLoading = false;
          state.error = null;
          state.isLoaded = true;
        },
      )
      .addCase(
        fetchConfiguration.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload;
        },
      );
  },
});

export const {
  setReviewDamageModified,
  setReviewPointOfImpactModified,
  resetReviewUIFlowState,
  setReviewMediaUpdated,
  setCollisionTypeUpdated,
  setCollisionSubTypeUpdated,
} = configurationSlice.actions;
export default configurationSlice.reducer;
